'use client';

import { useState } from 'react';

import Link from 'next/link';

import { NavigationMenu, ScrollArea, Skeleton } from '@holistic/components';
import {
  ChevronRight,
  ClipboardList,
  ShieldCheck,
} from '@holistic/components/icons';

import type { NavCategory, Navigation } from '@/types/navigation';
import type { LucideIcon } from '@holistic/components/icons';

import { tamirQuickLinks } from '../../constants/nav-list';

const quickLinkIcons: Record<string, LucideIcon> = {
  '/sorgula': ClipboardList,
  '/profilim/garantilerim': ShieldCheck,
};

/**
 * Sol sütun (kategoriler) sayfayla birlikte gelen `categories`'ten hemen
 * çiziliyor; marka/seri/model ağacı (`tree`) istemcide yükleniyor, gelene kadar
 * sağda iskelet görünüyor.
 */
export function TamirMegaMenu({
  categories,
  tree,
}: {
  categories: NavCategory[];
  tree: Navigation[] | null;
}) {
  const [activeId, setActiveId] = useState(categories[0]?.categoryId ?? '');
  const activeCategory =
    tree?.find((c) => c.categoryId === activeId) ?? tree?.[0];

  return (
    <div className="container-page flex flex-col">
      <div className="flex py-6">
        <ul className="w-56 shrink-0 space-y-1 border-r border-gray-100 pr-4">
          {categories.map((cat) => {
            const isActive = cat.categoryId === activeId;
            return (
              <li key={cat.categoryId}>
                <NavigationMenu.Link asChild>
                  <Link
                    href={`/talep?category=${cat.categoryId}`}
                    onMouseEnter={() => setActiveId(cat.categoryId)}
                    className={`block rounded-xl px-3 py-2 text-sm transition-all duration-200 ${
                      isActive
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    {cat.categoryName}
                  </Link>
                </NavigationMenu.Link>
              </li>
            );
          })}
        </ul>
        {/* max-h Root'a verilemiyor: Radix viewport'u `size-full`, yani
            height:100%. Root'un yuksekligi auto oldugu icin bu auto'ya dusuyor
            ve icerik scroll yerine kirpiliyor. Sinir dogrudan viewport'ta. */}
        <ScrollArea className="flex-1 [&>[data-slot=scroll-area-viewport]]:max-h-[50vh]">
          {/* grid-cols-4 degil: grid satir kilitliyor, her satirin yuksekligi
              en uzun hucre kadar oluyordu ve kisa markalarin altinda yuzlerce
              piksel bosluk kaliyordu. CSS multi-column bloklari akitarak
              paketliyor, satir hizasi diye bir sey kalmiyor. */}
          <div className="columns-4 gap-x-8 pr-4 pl-8">
            {!tree && <MegaMenuSkeleton />}
            {activeCategory?.brands.map((brand) => {
              const brandHref = `/talep?category=${activeCategory.categoryId}&brand=${brand.brandId}`;
              return (
                /* break-inside-avoid: marka blogu sutunlar arasinda ikiye
                 bolunmesin. Dikey aralik mb-6'dan geliyor, multi-column'da
                 row-gap yok. */
                <div
                  key={brand.brandId}
                  className="mb-6 flex break-inside-avoid flex-col gap-4"
                >
                  {/* Marka = kalın ana başlık */}
                  <NavigationMenu.Link asChild>
                    <Link
                      href={brandHref}
                      className="hover:text-primary w-fit p-0! text-sm font-bold text-gray-900 transition-colors hover:bg-transparent!"
                    >
                      {brand.brandName}
                    </Link>
                  </NavigationMenu.Link>
                  {brand.productLines.length > 0 ? (
                    <div className="flex flex-col gap-4">
                      {brand.productLines.map((line) => {
                        const lineHref = `${brandHref}&series=${line.productLineId}`;
                        return (
                          <div
                            key={line.productLineId}
                            className="flex flex-col gap-1.5"
                          >
                            {/* Ürün grubu = renkli, chevron'lu link başlık */}
                            <NavigationMenu.Link asChild>
                              <Link
                                href={lineHref}
                                className="text-primary hover:text-primary/70 w-fit gap-0.5 p-0! text-xs font-semibold transition-colors hover:bg-transparent!"
                              >
                                {line.productLineName}
                                <ChevronRight size={13} />
                              </Link>
                            </NavigationMenu.Link>
                            <ul className="flex flex-col gap-0.5">
                              {line.topModels.map((model) => (
                                <li key={model.id}>
                                  <NavigationMenu.Link asChild>
                                    <Link
                                      href={`${lineHref}&model=${model.id}`}
                                      className="hover:text-primary block w-fit px-0 py-0.5 text-xs text-gray-500 transition-colors hover:bg-transparent!"
                                    >
                                      {model.name}
                                    </Link>
                                  </NavigationMenu.Link>
                                </li>
                              ))}
                              {line.totalModelCount > line.topModels.length && (
                                <li>
                                  <NavigationMenu.Link asChild>
                                    <Link
                                      href={lineHref}
                                      className="hover:text-primary mt-1 w-fit gap-0.5 p-0! text-xs font-semibold text-gray-700 transition-colors hover:bg-transparent!"
                                    >
                                      Daha Fazla Gör
                                      <ChevronRight size={13} />
                                    </Link>
                                  </NavigationMenu.Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="flex flex-col gap-0.5">
                      {brand.topModels.map((model) => (
                        <li key={model.id}>
                          <NavigationMenu.Link asChild>
                            <Link
                              href={`${brandHref}&model=${model.id}`}
                              className="hover:text-primary block w-fit px-0 py-0.5 text-xs text-gray-500 transition-colors hover:bg-transparent!"
                            >
                              {model.name}
                            </Link>
                          </NavigationMenu.Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 py-4">
        <span className="mr-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
          Hızlı Erişim
        </span>
        {tamirQuickLinks.map(({ href, label }) => {
          const Icon = quickLinkIcons[href];
          return (
            <NavigationMenu.Link asChild key={href}>
              <Link
                href={href}
                className="hover:text-primary hover:bg-primary/5 hover:border-primary/30 flex items-center gap-1.5 rounded-full border border-gray-200 px-2 py-1.5 text-xs text-gray-600 transition-all duration-200"
              >
                {Icon && <Icon size={12} />}
                {label}
              </Link>
            </NavigationMenu.Link>
          );
        })}
      </div>
    </div>
  );
}

const MegaMenuSkeleton = () =>
  Array.from({ length: 8 }).map((_, index) => (
    <div key={index} className="mb-6 flex break-inside-avoid flex-col gap-2">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-3 w-20" />
    </div>
  ));
